import { axios } from 'Fetcher'
// Module, not the 'Lib' barrel — the barrel reaches store.ts and closes a cycle.
import { getCookiesMap, getCurrentVisitUrl, getCurrentVisitUrlParams, getLocalId } from 'Lib/AppUtils'
import { getTrackedEmail } from 'Lib/TrackingIdentity'
import { Store } from 'redux'
import { ONBOARDING_STEPS } from 'types/onboardingTypes'
import { v4 as uuidv4 } from 'uuid'

// Lazy: __DEV__ is undefined at module scope (Sentry.service loads before ./globals).
const TIKTOK_PROD_PIXEL_ID = 'CRR90QBC77U41Q43OPRG'
const TIKTOK_DEV_PIXEL_ID = 'CRR94A3C77U3OBON6VTG'
const getTikTokPixelId = () => __DEV__ ? TIKTOK_DEV_PIXEL_ID : TIKTOK_PROD_PIXEL_ID

const TIKTOK_SCRIPT_ID = 'tiktok-pixel'
const TIKTOK_SCRIPT_SRC = 'https://analytics.tiktok.com/i18n/pixel/events.js'
const TIKTOK_QUEUE_NAME = 'ttq'
const TTAPI_ROUTING_PATH = '/api/collect/ttapi'

// Deferred onto the queue until events.js drains it.
const TIKTOK_QUEUE_METHODS = [
  'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
  'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent',
  'revokeConsent', 'grantConsent'
]

type AnyFunction = (...args: any[]) => any

let store: Store | null = null
let currentEmail: string | null = null
let cookieConsentGranted: boolean | null = null
let pixelLoaded = false

// Consent-gated: loading the SDK at all writes _ttp/_tt_enable_cookie/ttcsid.
const loadTikTokPixel = () => {
  if (pixelLoaded) return
  pixelLoaded = true

  window.ttq?.load?.(getTikTokPixelId())
}

// Plain address: the SDK hashes it. Safe to call repeatedly; the guard is dedup.
const identifyTikTokUser = (email: string) => {
  if (currentEmail === email) return

  currentEmail = email
  window.ttq?.identify?.({ email, external_id: getLocalId() })
}

const withStoredUserData = <Func extends AnyFunction>(fn: Func) => {
  return (...args: Parameters<Func>) => {
    const trackedEmail = getTrackedEmail(store)

    if (trackedEmail) {
      identifyTikTokUser(trackedEmail)
    }

    fn(...args)
  }
}

// Current visit only — cross-session attribution belongs to the _ttp cookie.
const getTikTokClickId = (): string | null => getCurrentVisitUrlParams().get('ttclid')

// Written by the pixel SDK once consent is granted; TikTok matches on it.
const getTikTokCookieId = (): string | null => getCookiesMap().get('_ttp') ?? null

// Same-origin referrers are our own URLs and may still hold unsanitized secrets.
const getExternalReferrer = (): string | undefined => {
  if (!document.referrer) return undefined

  try {
    return new URL(document.referrer).origin === location.origin ? undefined : document.referrer
  } catch {
    return undefined
  }
}

// `eventId` must match the pixel's: TikTok dedups on (event_source_id, event,
// event_id). Email goes plain to our own backend, which hashes it.
const sendTikTokConversionsEvent = async (event: string, properties: Record<string, any>, eventId: string) => {
  // Server-side delivery is as consent-bound as the pixel.
  if (cookieConsentGranted !== true) {
    return
  }

  try {
    await axios.post(TTAPI_ROUTING_PATH, {
      event,
      event_id: eventId,
      // Sanitized + current-visit: location.href can still hold the payment token.
      url: getCurrentVisitUrl(),
      referrer: getExternalReferrer(),
      properties,
      user: {
        email: getTrackedEmail(store),
        external_id: getLocalId(),
        ttclid: getTikTokClickId(),
        ttp: getTikTokCookieId()
      }
    })
  } catch (error: any) {
    // Never rethrow — must not break the purchase flow. Sentry only off __DEV__:
    // a local run without the functions emulator would report every event.
    logError('web_tiktok_eventsApiError', error?.response?.data ?? error?.message ?? error, { event }, !__DEV__)
  }
}

// `eventId` pins the id shared with the Events API; omitted for pixel-only events.
export const trackTikTokEvent = withStoredUserData((event: string, properties: Record<string, any> = {}, eventId?: string) => {
  window.ttq?.track?.(event, properties, { event_id: eventId ?? uuidv4() })
})

// Landing view only: the SDK's HistoryObserver already fires per route change.
export const trackTikTokPageView = withStoredUserData(() => {
  window.ttq?.page?.()
})

type TikTokConversionEvent = [event: string, properties: Record<string, any>]

// content_type is 'product' because content_id carries a sku-like id, not a group.
const productProperties = (pricingId: string) => ({
  contents: [{ content_id: pricingId, content_name: pricingId }],
  content_type: 'product'
})

// Case-sensitive Events API 2.0 standard names; must match what the API sends.
const TIKTOK_CONVERSION_EVENTS: Record<string, (params: Record<string, any>) => TikTokConversionEvent> = {
  web_purchaseSuccess: params => ['Purchase', {
    ...productProperties(params.pricingId),
    value: params.value,
    currency: params.currency
  }],
  web_startCheckout: params => ['InitiateCheckout', {
    ...productProperties(params.pricingId),
    value: params.price,
    currency: params.currency
  }],
  // No product properties: signup happens before checkout, so the call sites pass
  // no pricing params and CompleteRegistration has no recommended ones.
  web_signUpSuccess: () => ['CompleteRegistration', {}]
}

export const trackTikTokConversionEvent = (eventName: AnalyticEvents | string, params: Record<string, any> = {}) => {
  const buildEvent = TIKTOK_CONVERSION_EVENTS[eventName]

  if (!buildEvent) {
    return
  }

  try {
    const [event, properties] = buildEvent(params)
    // One id, both channels — this is what lets TikTok dedup the pair.
    const eventId = uuidv4()

    trackTikTokEvent(event, properties, eventId)
    void sendTikTokConversionsEvent(event, properties, eventId)
  } catch (error) {
    logError('web_tiktok_trackError', error, { eventName }, true)
  }
}

// Reporting/audiences only — TikTok cannot optimize on custom events.
export const TIKTOK_STEP_EVENTS: Partial<Record<ONBOARDING_STEPS, string>> = {
  gender: 'CommitGender',
  reinforcement_scientific: 'CommitReinforcementScientific',
  bmi: 'CommitSummary',
  email: 'CommitNewsletter',
  receive_email: 'CommitReceiveNewsletter'
}

export const handleTikTokStepTrack = (stepId: ONBOARDING_STEPS) => {
  const event = TIKTOK_STEP_EVENTS[stepId]

  if (event) {
    trackTikTokEvent(event)
  }
}

export const grantTikTokConsent = () => {
  if (cookieConsentGranted === true) {
    return
  }

  cookieConsentGranted = true

  try {
    loadTikTokPixel()
    window.ttq?.grantConsent?.()
    // HistoryObserver only covers route changes, so fire the current view.
    trackTikTokPageView()
  } catch (error) {
    logError('web_tiktok_grantConsentError', error, {}, true)
  }
}

// revokeConsent() is the SDK's kill switch; detaching the script would do nothing.
export const revokeTikTokConsent = () => {
  if (cookieConsentGranted === false) {
    return
  }

  cookieConsentGranted = false

  try {
    window.ttq?.revokeConsent?.()
  } catch (error) {
    logError('web_tiktok_revokeConsentError', error, {}, true)
  }
}

export function initTikTokPixel(newStore: Store) {
  store = newStore

  // A stale cached index.html may still define the queue.
  if (window.ttq) return

  const ttq: TikTokPixelQueue = []

  ttq.methods = TIKTOK_QUEUE_METHODS
  ttq.setAndDefer = (queue: TikTokPixelQueue, methodName: string) => {
    queue[methodName] = (...args: any[]) => {
      queue.push([methodName, ...args])
    }
  }
  ttq.methods.forEach(methodName => ttq.setAndDefer?.(ttq, methodName))

  ttq.instance = (id: string) => {
    const instanceQueue: TikTokPixelQueue = ttq._i?.[id] ?? []
    ttq.methods?.forEach(methodName => ttq.setAndDefer?.(instanceQueue, methodName))
    return instanceQueue
  }

  ttq.load = (id: string, options?: Record<string, any>) => {
    ttq._i = ttq._i ?? {}
    ttq._i[id] = []
    ttq._i[id]._u = TIKTOK_SCRIPT_SRC
    ttq._t = ttq._t ?? {}
    ttq._t[id] = Date.now()
    ttq._o = ttq._o ?? {}
    ttq._o[id] = options ?? {}

    const script = document.createElement('script')
    script.async = true
    script.id = TIKTOK_SCRIPT_ID
    script.src = `${TIKTOK_SCRIPT_SRC}?sdkid=${id}&lib=${TIKTOK_QUEUE_NAME}`

    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
  }

  window.TiktokAnalyticsObject = TIKTOK_QUEUE_NAME
  window.ttq = ttq
}
